import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyFiltered } from "@/components/EmptyFiltered";
import { FacturaFormSheet } from "@/components/qbo/FacturaFormSheet";
import { EstadoIntegracionQBO } from "@/components/qbo/EstadoIntegracionQBO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FileText, Download, FileSpreadsheet } from "lucide-react";

const RESULTADO_LABELS = { creada: "Creada", simulada: "Simulada", rechazada: "Rechazada", error: "Error" };
const RESULTADO_CLASSES = {
  creada: "bg-green-50 text-green-700 border-green-200", simulada: "bg-blue-50 text-blue-700 border-blue-200",
  rechazada: "bg-amber-50 text-amber-700 border-amber-200", error: "bg-red-50 text-red-700 border-red-200",
};

function PorFacturar({ companyId }) {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0 });
  const [filters, setFilters] = useState({ fecha_desde: "", fecha_hasta: "" });
  const [sheetLoad, setSheetLoad] = useState(undefined);
  const [openSheet, setOpenSheet] = useState(false);

  const load = useCallback(async () => {
    if (!companyId) return;
    try {
      const params = { company_id: companyId };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get("/facturacion-qbo/loads", { params });
      setData(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, [companyId, filters]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const loadId = searchParams.get("load_id");
    if (loadId && companyId) {
      setSheetLoad({ id: loadId, numero_load: loadId });
      setOpenSheet(true);
    }
  }, [searchParams, companyId]);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end gap-2">
        <Input type="date" value={filters.fecha_desde} onChange={(e) => setFilters((f) => ({ ...f, fecha_desde: e.target.value }))} data-testid="facturacion-filter-fecha-desde" />
        <Input type="date" value={filters.fecha_hasta} onChange={(e) => setFilters((f) => ({ ...f, fecha_hasta: e.target.value }))} data-testid="facturacion-filter-fecha-hasta" />
        <Button variant="outline" className="ml-auto gap-2" onClick={() => { setSheetLoad(null); setOpenSheet(true); }} data-testid="factura-directa-btn">
          <FileText className="h-4 w-4" /> Factura directa (sin viaje)
        </Button>
      </div>

      <div className="overflow-x-auto rounded-md border border-border bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Load</TableHead>
              <TableHead>Cliente</TableHead>
              <TableHead>Ruta</TableHead>
              <TableHead>Pickup</TableHead>
              <TableHead>Cobro</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.items.map((l) => (
              <TableRow key={l.id} data-testid={`facturacion-row-${l.numero_load}`}>
                <TableCell className="font-mono text-xs font-semibold">{l.numero_load}</TableCell>
                <TableCell className="max-w-[160px] truncate text-sm">{l.client_nombre}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{l.origen_ciudad} → {l.destino_ciudad}</TableCell>
                <TableCell className="font-mono text-xs">{l.fecha_pickup}</TableCell>
                <TableCell className="font-mono text-xs">
                  ${l.monto_cobro?.toFixed?.(2)} {l.moneda_cobro}
                  {l.monto_sugerido && (
                    <Badge className="ml-2 border border-amber-200 bg-amber-50 text-amber-700" data-testid={`facturacion-sugerido-${l.numero_load}`}>Tarifa sugerida</Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="outline" onClick={() => { setSheetLoad(l); setOpenSheet(true); }} data-testid={`facturar-btn-${l.numero_load}`}>
                    Facturar
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {data.items.length === 0 && (
              <EmptyFiltered colSpan={6} onClear={() => setFilters({ fecha_desde: "", fecha_hasta: "" })} testId="facturacion-empty"
                mensaje="No hay viajes de Álvarez listos para facturar con estos filtros." />
            )}
          </TableBody>
        </Table>
      </div>

      <FacturaFormSheet open={openSheet} onOpenChange={setOpenSheet} companyId={companyId} load={sheetLoad}
        onCreated={load} />
    </div>
  );
}

// P39 (2026-09-28): tickets de Álvarez de hace más de 14 días que no tienen factura en QuickBooks.
// Revisando a mano: la mayoría nunca se facturó y alguno está facturado con otra referencia; por eso
// se muestran las facturas de ese cliente que no corresponden a ningún viaje ("posible factura").
function SinFactura() {
  const [data, setData] = useState({ items: [], fecha: null, dias: 14 });
  useEffect(() => {
    api.get("/dat/alvarez/sin-factura").then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, []);
  return (
    <div>
      <p className="mb-3 text-sm text-muted-foreground" data-testid="sin-factura-resumen">
        {data.items.length} viaje(s) de ALVAREZ TRUCKING en DAT de hace más de {data.dias} días sin factura en QuickBooks
        (ni por el folio "V #" ni por la referencia). Revisá si falta cobrarlos o si están facturados con otra referencia.
        {data.fecha ? ` Actualizado: ${data.fecha.slice(0, 16).replace("T", " ")}.` : ""}
      </p>
      <div className="overflow-x-auto rounded-md border border-border bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Fecha</TableHead>
              <TableHead>Ticket DAT</TableHead>
              <TableHead>Referencia</TableHead>
              <TableHead>Ruta</TableHead>
              <TableHead>Cliente probable</TableHead>
              <TableHead>Viaje</TableHead>
              <TableHead>Posible factura en QuickBooks</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.items.map((t) => (
              <TableRow key={t.dat_ticket_id} data-testid={`sin-factura-row-${t.folio}`}>
                <TableCell className="font-mono text-xs">{t.fecha} <span className="text-muted-foreground">({t.dias} d)</span></TableCell>
                <TableCell className="font-mono text-xs">{[...new Set(t.folios || [t.folio])].join(", ")}</TableCell>
                <TableCell className="text-xs">{t.referencia || "—"}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{t.ruta || "—"}</TableCell>
                <TableCell className="max-w-[160px] truncate text-xs">{t.cliente || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{t.viaje || "—"}</TableCell>
                <TableCell className="text-xs">
                  {(t.posibles_facturas || []).length === 0 ? "—" : t.posibles_facturas.map((f) => (
                    <div key={f.doc}>#{f.doc} · {f.fecha} · ${Number(f.monto || 0).toFixed(2)} · {f.memo || f.item}</div>
                  ))}
                </TableCell>
              </TableRow>
            ))}
            {data.items.length === 0 && (
              <TableRow><TableCell colSpan={7} className="py-10 text-center text-muted-foreground">No hay tickets de Álvarez sin factura.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// P41 (2026-10-02): "las Refs AL no nos cuadran". Cada factura de QuickBooks de Álvarez tiene que tener su
// viaje. Por tramos de fechas: se simula, y si está bien se cargan los que faltan (desde la factura).
const ESTADO_CUADRE = {
  ya_existe: ["Ya tiene viaje", "bg-green-50 text-green-700 border-green-200"],
  ticket_con_viaje: ["El ticket ya tiene viaje", "bg-green-50 text-green-700 border-green-200"],
  se_crearia: ["Falta: se crearía", "bg-amber-50 text-amber-700 border-amber-200"],
  creado: ["Creado", "bg-blue-50 text-blue-700 border-blue-200"],
  misma_referencia: ["Hay un viaje con esa referencia: ponele el N° de factura", "bg-amber-50 text-amber-700 border-amber-200"],
  sin_compania: ["Falta la compañía AL", "bg-red-50 text-red-700 border-red-200"],
  error: ["No se pudo crear", "bg-red-50 text-red-700 border-red-200"],
};
const CONCILIACION_LABELS = {
  cobros_registrados: "cobros registrados", cobros_revertidos: "cobros revertidos", montos_corregidos: "montos corregidos",
  clientes_actualizados: "clientes con el nombre de QuickBooks", cobros_inconsistentes: "cobrados en la plataforma con saldo en QuickBooks",
  viajes_con_varias_facturas: "viajes con dos facturas (sin tocar)", omitido_otra_corrida_en_curso: "vez omitida (otra carga en curso)",
  omitidos_dat_manda: "vinculados a DAT (manda DAT)", montos_no_tocados_factura_compartida: "facturas en dos viajes (monto sin tocar)",
  facturas_anuladas_quitadas: "viajes con una factura anulada en QuickBooks (corregidos)",   // P45
};

function CuadreQBO() {
  const [rango, setRango] = useState({ desde: "", hasta: new Date().toISOString().slice(0, 10) });
  const [job, setJob] = useState(null);
  const [conc, setConc] = useState(null);
  const [soloFaltan, setSoloFaltan] = useState(false);
  const [lanzando, setLanzando] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const [h, c] = await Promise.all([api.get("/dat/alvarez/historial"), api.get("/dat/alvarez/conciliacion")]);
      setJob(h.data?.estado ? h.data : null);
      setConc(c.data?.fecha ? c.data : null);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);
  // Una carga que quedó "corriendo" más de 20 minutos se cayó (reinicio del servidor): se puede relanzar.
  const corriendo = job?.estado === "corriendo" && Date.now() - Date.parse(job.inicio || 0) < 20 * 60 * 1000;
  useEffect(() => {
    if (!corriendo) return undefined;
    const t = setInterval(cargar, 4000);
    return () => clearInterval(t);
  }, [corriendo, cargar]);

  const lanzar = async (aplicar) => {
    setLanzando(true);
    try {
      const { data } = await api.post("/dat/alvarez/historial", { ...rango, aplicar });
      setJob(data);
      toast.success(aplicar ? "Cargando los viajes que faltan…" : "Simulando…");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLanzando(false); }
  };

  const r = job?.resumen || {};
  const simulado = job && !job.aplicar && job.estado === "ok" && job.desde === rango.desde && job.hasta === rango.hasta;
  const filas = (job?.filas || []).filter((f) => !soloFaltan || !["ya_existe", "ticket_con_viaje"].includes(f.estado));
  return (
    <div data-testid="cuadre-qbo">
      <p className="mb-3 text-sm text-muted-foreground">
        Cada factura de Álvarez en QuickBooks tiene que tener su viaje en la plataforma. Elegí un tramo de fechas (máximo 62
        días), simulá y, si está bien, cargá los que faltan: se crean con el cliente, el monto y el número de la factura, y la
        ruta, unidad, placas y operador del ticket de DAT. No escribe nada en QuickBooks.
      </p>
      <div className="mb-4 flex flex-wrap items-end gap-2">
        <Input type="date" className="w-44" value={rango.desde} onChange={(e) => setRango((x) => ({ ...x, desde: e.target.value }))} data-testid="cuadre-desde" />
        <Input type="date" className="w-44" value={rango.hasta} onChange={(e) => setRango((x) => ({ ...x, hasta: e.target.value }))} data-testid="cuadre-hasta" />
        <Button variant="outline" disabled={!rango.desde || !rango.hasta || lanzando || corriendo} onClick={() => lanzar(false)} data-testid="cuadre-simular">
          Simular
        </Button>
        <Button disabled={!simulado || !r.se_crearia || lanzando} onClick={() => lanzar(true)} data-testid="cuadre-cargar">
          Cargar los {r.se_crearia || 0} que faltan
        </Button>
      </div>

      {job && (
        <div className="mb-3 text-sm" data-testid="cuadre-estado">
          {corriendo && <span className="text-muted-foreground">{job.aplicar ? "Cargando" : "Simulando"} del {job.desde} al {job.hasta}… (puede tardar un minuto)</span>}
          {job.estado === "corriendo" && !corriendo && <span className="text-amber-700">La carga anterior no terminó (se reinició el servidor): volvé a lanzarla.</span>}
          {job.estado === "error" && <span className="text-red-700">No se pudo completar: {job.error}</span>}
          {job.estado === "aviso" && <span className="text-amber-700">{job.error}</span>}
          {job.estado === "ok" && (
            <span data-testid="cuadre-resumen">
              {job.aplicar ? "Carga" : "Simulación"} del {job.desde} al {job.hasta}: <b>{r.facturas || 0}</b> facturas de tickets de DAT ·{" "}
              <b>{(r.ya_existe || 0) + (r.ticket_con_viaje || 0)}</b> ya tienen viaje ·{" "}
              {job.aplicar ? <><b>{r.creado || 0}</b> viajes creados</> : <><b>{r.se_crearia || 0}</b> faltan</>} ·{" "}
              {r.misma_referencia ? <>{r.misma_referencia} con la misma referencia en otro viaje ·{" "}</> : null}
              {r.facturas_sin_ticket || 0} facturas sin ticket de DAT ({r.facturas_sin_ticket_sin_viaje || 0} sin viaje).
            </span>
          )}
        </div>
      )}

      {filas.length > 0 && (
        <>
          <label className="mb-2 flex items-center gap-2 text-xs text-muted-foreground">
            <input type="checkbox" checked={soloFaltan} onChange={(e) => setSoloFaltan(e.target.checked)} data-testid="cuadre-solo-faltan" />
            Solo las que faltan
          </label>
          <div className="mb-5 overflow-x-auto rounded-md border border-border bg-white">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha</TableHead><TableHead>Ticket DAT</TableHead><TableHead>Referencia</TableHead><TableHead>Ruta</TableHead>
                  <TableHead>Factura</TableHead><TableHead>Cliente en QuickBooks</TableHead><TableHead>Monto</TableHead>
                  <TableHead>Estado</TableHead><TableHead>Viaje</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filas.map((f) => (
                  <TableRow key={f.doc} data-testid={`cuadre-row-${f.doc}`}>
                    <TableCell className="font-mono text-xs">{f.fecha}</TableCell>
                    <TableCell className="font-mono text-xs">{[...new Set(f.folios || [])].join(", ")}</TableCell>
                    <TableCell className="text-xs">{f.referencia || "—"}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{f.ruta || "—"}</TableCell>
                    <TableCell className="font-mono text-xs">#{f.doc}</TableCell>
                    <TableCell className="max-w-[180px] truncate text-xs">{f.cliente || "—"}</TableCell>
                    <TableCell className="font-mono text-xs">${Number(f.monto || 0).toFixed(2)}{Number(f.saldo) <= 0.01 && Number(f.monto) > 0 ? " · pagada" : ""}</TableCell>
                    <TableCell><Badge className={`border ${(ESTADO_CUADRE[f.estado] || [])[1] || ""}`}>{(ESTADO_CUADRE[f.estado] || [f.estado])[0]}</Badge></TableCell>
                    <TableCell className="font-mono text-xs">{f.viaje || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}

      {(job?.sin_ticket || []).length > 0 && (
        <details className="mb-5 rounded-md border border-border bg-white p-3" data-testid="cuadre-sin-ticket">
          <summary className="cursor-pointer text-sm">Facturas del tramo que no corresponden a un ticket de DAT ({job.sin_ticket.length})</summary>
          <p className="mt-2 text-xs text-muted-foreground">Cargos sueltos, fletes sin ticket o facturas con otra referencia: no se crean viajes desde acá.</p>
          <div className="mt-2 space-y-1 text-xs">
            {job.sin_ticket.map((x) => (
              <div key={x.doc} className="font-mono">#{x.doc} · {x.fecha} · {x.cliente} · ${Number(x.monto || 0).toFixed(2)} · {x.memo || "—"} · {x.viaje ? `viaje ${x.viaje}` : "sin viaje"}</div>
            ))}
          </div>
        </details>
      )}

      {conc && (
        <div className="rounded-md border border-border bg-white p-3 text-sm" data-testid="cuadre-conciliacion">
          <p className="font-medium">Conciliación automática con QuickBooks (cada 6 horas) — {conc.fecha.slice(0, 16).replace("T", " ")}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            {Object.entries(conc.stats || {}).filter(([k, v]) => CONCILIACION_LABELS[k] && v).map(([k, v]) => `${v} ${CONCILIACION_LABELS[k]}`).join(" · ") || "Sin cambios."}
            {conc.creados?.creado ? ` · ${conc.creados.creado} viajes creados desde facturas` : ""}
          </p>
          {(conc.avisos || []).length > 0 && (
            <ul className="mt-2 list-disc pl-5 text-xs text-amber-800">{conc.avisos.map((a) => <li key={a}>{a}</li>)}</ul>
          )}
        </div>
      )}
    </div>
  );
}

function Bitacora({ companyId }) {
  const [data, setData] = useState({ items: [], total: 0 });
  const [resultado, setResultado] = useState("all");

  const load = useCallback(async () => {
    try {
      const params = { company_id: companyId };
      if (resultado !== "all") params.resultado = resultado;
      const { data } = await api.get("/facturacion-qbo/bitacora", { params });
      setData(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, [companyId, resultado]);

  useEffect(() => { load(); }, [load]);

  const exportFile = async (fmt) => {
    const res = await api.get(`/facturacion-qbo/bitacora/export/${fmt}`, { params: { company_id: companyId }, responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url; a.download = `bitacora_facturacion_qbo.${fmt}`; a.click(); a.remove();
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end gap-2">
        <Select value={resultado} onValueChange={setResultado}>
          <SelectTrigger className="w-48" data-testid="bitacora-filter-resultado"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los resultados</SelectItem>
            {Object.entries(RESULTADO_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" size="sm" onClick={() => exportFile("csv")} className="gap-1" data-testid="bitacora-export-csv"><Download className="h-4 w-4" /> CSV</Button>
          <Button variant="outline" size="sm" onClick={() => exportFile("xlsx")} className="gap-1" data-testid="bitacora-export-xlsx"><FileSpreadsheet className="h-4 w-4" /> Excel</Button>
        </div>
      </div>
      <div className="overflow-x-auto rounded-md border border-border bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Fecha</TableHead>
              <TableHead>Usuario</TableHead>
              <TableHead>Load</TableHead>
              <TableHead>Resultado</TableHead>
              <TableHead>Factura QBO</TableHead>
              <TableHead>Mensaje</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.items.map((b) => (
              <TableRow key={b.id} data-testid={`bitacora-row-${b.id}`}>
                <TableCell className="font-mono text-xs">{(b.fecha || "").slice(0, 19).replace("T", " ")}</TableCell>
                <TableCell className="text-xs">{b.usuario}</TableCell>
                <TableCell className="font-mono text-xs">{b.load_id || "—"}</TableCell>
                <TableCell><Badge className={`border ${RESULTADO_CLASSES[b.resultado] || ""}`}>{RESULTADO_LABELS[b.resultado] || b.resultado}</Badge></TableCell>
                <TableCell className="font-mono text-xs">{b.numero_factura_qbo || "—"}</TableCell>
                <TableCell className="max-w-[240px] truncate text-xs text-muted-foreground">{b.mensaje || "—"}</TableCell>
              </TableRow>
            ))}
            {data.items.length === 0 && (
              <TableRow><TableCell colSpan={6} className="py-10 text-center text-muted-foreground">Sin intentos de facturación todavía.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// P47: estado de cada línea de gasto de QuickBooks (simulación y carga).
const ESTADO_GASTO = {
  nuevo: ["Se carga", "bg-amber-50 text-amber-700 border-amber-200"],
  creado: ["Cargado", "bg-blue-50 text-blue-700 border-blue-200"],
  ya_cargado: ["Ya cargado", "bg-green-50 text-green-700 border-green-200"],
  actualizar: ["Se actualiza", "bg-amber-50 text-amber-700 border-amber-200"],
  actualizado: ["Actualizado", "bg-blue-50 text-blue-700 border-blue-200"],
  mover: ["Cambia de viaje", "bg-amber-50 text-amber-700 border-amber-200"],
  movido: ["Cambió de viaje", "bg-blue-50 text-blue-700 border-blue-200"],
  anular: ["Se anula", "bg-amber-50 text-amber-700 border-amber-200"],
  anulado: ["Anulado", "bg-blue-50 text-blue-700 border-blue-200"],
  viaje_cerrado: ["Viaje cerrado: no se toca", "bg-slate-50 text-slate-700 border-slate-200"],
  sin_clave: ["Sin embarque en la línea", "bg-red-50 text-red-700 border-red-200"],
  sin_viaje: ["El embarque no tiene viaje", "bg-red-50 text-red-700 border-red-200"],
  varios_viajes: ["Varios viajes con ese embarque", "bg-red-50 text-red-700 border-red-200"],
  error: ["No se pudo cargar", "bg-red-50 text-red-700 border-red-200"],
};
const GASTO_SIN_AMARRE = ["sin_clave", "sin_viaje", "varios_viajes", "viaje_cerrado", "error"];

function GastosQBO() {
  const [rango, setRango] = useState({ desde: "", hasta: new Date().toISOString().slice(0, 10) });
  const [job, setJob] = useState(null);
  const [soloSinAmarre, setSoloSinAmarre] = useState(false);
  const [lanzando, setLanzando] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const { data } = await api.get("/dat/alvarez/gastos");
      setJob(data?.estado ? data : null);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);
  const corriendo = job?.estado === "corriendo" && Date.now() - Date.parse(job.inicio || 0) < 20 * 60 * 1000;
  useEffect(() => {
    if (!corriendo) return undefined;
    const t = setInterval(cargar, 4000);
    return () => clearInterval(t);
  }, [corriendo, cargar]);

  const lanzar = async (aplicar) => {
    setLanzando(true);
    try {
      const { data } = await api.post("/dat/alvarez/gastos", { ...rango, aplicar });
      setJob(data);
      toast.success(aplicar ? "Cargando los gastos en los viajes…" : "Simulando…");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLanzando(false); }
  };

  const r = job?.resumen || {};
  const simulado = job && !job.aplicar && job.estado === "ok" && job.desde === rango.desde && job.hasta === rango.hasta;
  const cambios = (r.nuevo || 0) + (r.actualizar || 0) + (r.mover || 0) + (r.anular || 0);
  const filas = (job?.filas || []).filter((f) => !soloSinAmarre || GASTO_SIN_AMARRE.includes(f.estado));
  return (
    <div data-testid="gastos-qbo">
      <p className="mb-3 text-sm text-muted-foreground">
        Lo que se le paga a cada carrier en QuickBooks (gastos de LOGISTICA EXTERNA y PAGO FLETES LOGISTICA) se carga como
        costo del viaje, por el número de tráfico (T0962, AL-0327) o el folio de DAT escrito en cada línea. Elegí un tramo
        (máximo 62 días), simulá y, si está bien, cargá. No escribe nada en QuickBooks.
      </p>
      <div className="mb-4 flex flex-wrap items-end gap-2">
        <Input type="date" className="w-44" value={rango.desde} onChange={(e) => setRango((x) => ({ ...x, desde: e.target.value }))} data-testid="gastos-desde" />
        <Input type="date" className="w-44" value={rango.hasta} onChange={(e) => setRango((x) => ({ ...x, hasta: e.target.value }))} data-testid="gastos-hasta" />
        <Button variant="outline" disabled={!rango.desde || !rango.hasta || lanzando || corriendo} onClick={() => lanzar(false)} data-testid="gastos-simular">
          Simular
        </Button>
        <Button disabled={!simulado || !cambios || lanzando} onClick={() => lanzar(true)} data-testid="gastos-cargar">
          Cargar {cambios} cambios
        </Button>
      </div>

      {job && (
        <div className="mb-3 text-sm" data-testid="gastos-estado">
          {corriendo && <span className="text-muted-foreground">{job.aplicar ? "Cargando" : "Simulando"} del {job.desde} al {job.hasta}… (puede tardar un minuto)</span>}
          {job.estado === "corriendo" && !corriendo && <span className="text-amber-700">La corrida anterior no terminó (se reinició el servidor): volvé a lanzarla.</span>}
          {job.estado === "error" && <span className="text-red-700">No se pudo completar: {job.error}</span>}
          {job.estado === "aviso" && <span className="text-amber-700">{job.error}</span>}
          {job.estado === "ok" && (
            <span data-testid="gastos-resumen">
              {job.aplicar ? "Carga" : "Simulación"} del {job.desde} al {job.hasta}: <b>{r.gastos || 0}</b> gastos · <b>{r.lineas || 0}</b> líneas de flete ·{" "}
              {job.aplicar
                ? <><b>{r.creado || 0}</b> cargadas · {r.actualizado || 0} actualizadas · {r.movido || 0} cambiaron de viaje · {r.anulado || 0} anuladas</>
                : <><b>{r.nuevo || 0}</b> se cargan (USD {Number(r.usd_nuevos || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}) · {r.actualizar || 0} se actualizan · {r.mover || 0} cambian de viaje · {r.anular || 0} se anulan</>}
              {" "}· {r.ya_cargado || 0} ya cargadas · <b>{r.sin_amarre || 0}</b> sin amarre · {r.viajes || 0} viajes.
              {!job.aplicar && r.se_cerrarian ? <> {r.se_cerrarian} viajes cobrados quedarían con todo pagado y la plataforma los cierra.</> : null}
            </span>
          )}
        </div>
      )}

      {(job?.filas || []).length > 0 && (
        <>
          <label className="mb-2 flex items-center gap-2 text-xs text-muted-foreground">
            <input type="checkbox" checked={soloSinAmarre} onChange={(e) => setSoloSinAmarre(e.target.checked)} data-testid="gastos-solo-sin-amarre" />
            Solo las que no se pudieron amarrar
          </label>
          <div className="mb-5 overflow-x-auto rounded-md border border-border bg-white">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha</TableHead><TableHead>Proveedor</TableHead><TableHead>Línea en QuickBooks</TableHead>
                  <TableHead>Embarque</TableHead><TableHead>Viaje</TableHead><TableHead>Monto</TableHead><TableHead>Estado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filas.slice(0, 500).map((f) => (
                  <TableRow key={`${f.entidad}-${f.txn_id}-${f.linea_id}`} data-testid={`gastos-row-${f.txn_id}-${f.linea_id}`}>
                    <TableCell className="font-mono text-xs">{f.fecha}</TableCell>
                    <TableCell className="max-w-[180px] truncate text-xs">{f.proveedor || "—"}</TableCell>
                    <TableCell className="max-w-[260px] truncate text-xs" title={f.memo ? `Memo: ${f.memo}` : ""}>{f.descripcion || "—"}</TableCell>
                    <TableCell className="font-mono text-xs">{(f.claves || []).map((c) => c[1]).join(", ") || "—"}{f.fuente === "memo" ? " (memo)" : ""}</TableCell>
                    <TableCell className="font-mono text-xs">{f.viaje || "—"}</TableCell>
                    <TableCell className="font-mono text-xs">{f.moneda} {Number(f.monto || 0).toFixed(2)}{f.pagado ? "" : " · por pagar"}</TableCell>
                    <TableCell><Badge className={`border ${(ESTADO_GASTO[f.estado] || [])[1] || ""}`}>{(ESTADO_GASTO[f.estado] || [f.estado])[0]}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {filas.length > 500 && <p className="text-xs text-muted-foreground">Se muestran 500 de {filas.length} líneas.</p>}
        </>
      )}
    </div>
  );
}

export default function FacturacionQBO() {
  const [companyId, setCompanyId] = useState(null);
  const { user } = useAuth();   // P39: la pestaña "Sin factura" es solo del Dueño (como la simulación de P37)

  useEffect(() => {
    api.get("/companies").then(({ data }) => {
      const al = data.find((c) => c.prefijo === "AL");
      setCompanyId(al?.id || null);
    }).catch(() => {});
  }, []);

  if (companyId === null) {
    return <div className="text-sm text-muted-foreground">Cargando...</div>;
  }

  return (
    <div data-testid="facturacion-qbo-page">
      <PageHeader title="Facturación QuickBooks" subtitle="Crea las facturas de Álvarez directo en QuickBooks — se guardan, nunca se envían." />
      <EstadoIntegracionQBO companyId={companyId} />
      <Tabs defaultValue="por-facturar">
        <TabsList>
          <TabsTrigger value="por-facturar" data-testid="facturacion-tab-por-facturar">Por facturar</TabsTrigger>
          <TabsTrigger value="bitacora" data-testid="facturacion-tab-bitacora">Bitácora</TabsTrigger>
          {user?.rol === "dueno" && (
            <TabsTrigger value="sin-factura" data-testid="facturacion-tab-sin-factura">Sin factura en QuickBooks</TabsTrigger>
          )}
          {user?.rol === "dueno" && (
            <TabsTrigger value="cuadre" data-testid="facturacion-tab-cuadre">Cuadre con QuickBooks</TabsTrigger>
          )}
          {user?.rol === "dueno" && (
            <TabsTrigger value="gastos" data-testid="facturacion-tab-gastos">Gastos de QuickBooks</TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="por-facturar" className="mt-4"><PorFacturar companyId={companyId} /></TabsContent>
        <TabsContent value="bitacora" className="mt-4"><Bitacora companyId={companyId} /></TabsContent>
        {user?.rol === "dueno" && (
          <TabsContent value="sin-factura" className="mt-4"><SinFactura /></TabsContent>
        )}
        {user?.rol === "dueno" && (
          <TabsContent value="cuadre" className="mt-4"><CuadreQBO /></TabsContent>
        )}
        {user?.rol === "dueno" && (
          <TabsContent value="gastos" className="mt-4"><GastosQBO /></TabsContent>
        )}
      </Tabs>
    </div>
  );
}
